/**
 * Supabase Storage image transformation helper.
 * Appends render params to Supabase storage URLs for on-the-fly resizing.
 *
 * For local /images/*.png paths, falls back to pre-built WebP thumbnails.
 */

const SUPABASE_STORAGE_HOST = "lzjamptnuzafnvfudcft.supabase.co/storage/v1/object/public/";

function isSupabaseUrl(url: string): boolean {
  return url.includes(SUPABASE_STORAGE_HOST);
}

/**
 * Convert a Supabase public storage URL to use the render endpoint with transforms.
 * /storage/v1/object/public/bucket/path → /storage/v1/render/image/public/bucket/path?width=W&format=webp
 */
function supabaseTransform(url: string, width: number): string {
  return url.replace(
    "/storage/v1/object/public/",
    "/storage/v1/render/image/public/"
  ) + `?width=${width}&resize=contain&format=origin`;
}

/**
 * Get a thumbnail-sized image URL (~400px wide).
 * Used in grids/cards where images are small.
 */
export function getThumbUrl(coverUrl: string | null): string | null {
  if (!coverUrl) return null;

  // Supabase storage → use render API
  if (isSupabaseUrl(coverUrl)) {
    return supabaseTransform(coverUrl, 400);
  }

  // Local /images/*.png → pre-built WebP
  const match = coverUrl.match(/^\/images\/([^/]+)\.png$/);
  if (match) return `/images/thumb/${match[1]}.webp`;

  return coverUrl;
}

/**
 * Get a full-size image URL (~800px wide).
 * Used in detail panels/modals.
 */
export function getFullUrl(coverUrl: string | null): string | null {
  if (!coverUrl) return null;

  if (isSupabaseUrl(coverUrl)) {
    return supabaseTransform(coverUrl, 800);
  }

  const match = coverUrl.match(/^\/images\/([^/]+)\.png$/);
  if (match) return `/images/full/${match[1]}.webp`;

  return coverUrl;
}

/**
 * Get a cover image URL for lesson cards (~600px wide).
 */
export function getLessonThumbUrl(coverUrl: string | null): string | null {
  if (!coverUrl) return null;

  if (isSupabaseUrl(coverUrl)) {
    return supabaseTransform(coverUrl, 600);
  }

  return coverUrl;
}
